import { useMemo, useState } from 'react';
import { Button, Container, Modal, ModalContent, ModalFooter, ModalHeader, ModalRoot } from '@pella/sash-framework';
import { normalizeAnalyticsRows } from '../../../utils/analytics';
import {
  useExportUserAnalyticsRows,
  useUserAnalyticsEngagementSummary,
  useUserAnalyticsFilterOptions,
  useUserAnalyticsRows,
  useUserAnalyticsRowsCount,
} from '../../../hooks/useMaintenanceCopilot/useMaintenanceCopilot';
import styles from './Analytics.module.css';

const PAGE_SIZE = 100;
const USAGE_LINE_CHART_WIDTH = 560;
const USAGE_LINE_CHART_HEIGHT = 220;
const USAGE_LINE_CHART_PADDING = { top: 20, right: 20, bottom: 36, left: 44 };

const formatDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString();
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';

  if (typeof timestamp === 'string') return timestamp;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const QA_ENGAGEMENT_DISPLAY = {
  SPENT_TIME: { label: 'Spent time*', toneClassName: styles.qaAssessmentSpent },
  PARTIAL: { label: 'Partial*', toneClassName: styles.qaAssessmentPartial },
  QUICK_PASS: { label: 'Quick pass*', toneClassName: styles.qaAssessmentQuick },
};

const getQaEngagementAssessment = (row) => QA_ENGAGEMENT_DISPLAY[row.qaEngagement] || null;

const getDayStart = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  date.setHours(0, 0, 0, 0);
  return date;
};

const formatShortDate = (value) => {
  if (!(value instanceof Date)) return '';
  return value.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getDaysBetween = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date) || startDate > endDate) return [];

  const startTime = startDate.getTime();
  const endTime = endDate.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const dayCount = Math.floor((endTime - startTime) / dayMs) + 1;

  return Array.from({ length: dayCount }, (_, index) => new Date(startTime + index * dayMs));
};

const normalizeFilterOptions = (values = [], selectedValue = '') => {
  const normalizedValues = Array.isArray(values)
    ? values.map((value) => (typeof value === 'string' ? value.trim() : '')).filter(Boolean)
    : [];

  const uniqueValues = [...new Set(normalizedValues)].toSorted((a, b) => a.localeCompare(b));
  const normalizedSelected = typeof selectedValue === 'string' ? selectedValue.trim() : '';

  if (normalizedSelected && !uniqueValues.includes(normalizedSelected)) {
    return [normalizedSelected, ...uniqueValues];
  }

  return uniqueValues;
};

const buildUsageLinePath = (points) => {
  if (!points.length) return '';

  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
};

const normalizeDateInput = (value) => {
  if (typeof value !== 'string') return undefined;

  const trimmedValue = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/u.test(trimmedValue) ? trimmedValue : undefined;
};

const downloadBlobAsFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const FilterChecklistDropdown = ({ label, allLabel, options, selectedValue, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return options;

    return options.filter((option) => option.toLowerCase().includes(needle));
  }, [options, searchTerm]);

  const summaryLabel = selectedValue || allLabel;
  const searchable = options.length > 7;

  return (
    <details className={styles.filterDropdown}>
      <summary className={styles.filterDropdownSummary} aria-label={label}>
        {summaryLabel}
      </summary>
      <div className={styles.filterDropdownPanel}>
        {searchable ? (
          <input
            type='text'
            className={styles.filterDropdownSearch}
            placeholder='Search'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        ) : null}
        <label className={styles.filterDropdownOption}>
          <input type='checkbox' checked={!selectedValue} onChange={() => onChange('')} />
          <span>{allLabel}</span>
        </label>
        {filteredOptions.length ? (
          filteredOptions.map((option) => (
            <label key={option} className={styles.filterDropdownOption}>
              <input type='checkbox' checked={selectedValue === option} onChange={() => onChange(option)} />
              <span>{option}</span>
            </label>
          ))
        ) : (
          <p className={styles.filterDropdownNoOptions}>No matches</p>
        )}
      </div>
    </details>
  );
};

/**
 * Display analytics rows and derived usage summaries.
 * @returns {import('react').ReactElement} The analytics page.
 */
const Analytics = () => {
  const [selectedQaRow, setSelectedQaRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const [employeeFilter, setEmployeeFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [serviceAreaFilter, setServiceAreaFilter] = useState('');
  const [serviceActionFilter, setServiceActionFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [appliedStartDateFilter, setAppliedStartDateFilter] = useState('');
  const [appliedEndDateFilter, setAppliedEndDateFilter] = useState('');
  const [dateInputResetToken, setDateInputResetToken] = useState(0);
  const [hoveredUsagePoint, setHoveredUsagePoint] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});

  const {
    data: analyticsRows,
    isError: isRowsError,
    isFetching: isRowsFetching,
    refetch: refetchRows,
  } = useUserAnalyticsRows({
    limit: PAGE_SIZE,
    offset: currentPage * PAGE_SIZE,
    filters: appliedFilters,
  });
  const {
    data: totalCountRaw = 0,
    isError: isCountError,
    isFetching: isCountFetching,
    refetch: refetchCount,
  } = useUserAnalyticsRowsCount({ filters: appliedFilters });
  const totalCount = typeof totalCountRaw === 'number' ? totalCountRaw : 0;
  const {
    data: engagementSummary,
    isError: isEngagementSummaryError,
    isFetching: isEngagementSummaryFetching,
    refetch: refetchEngagementSummary,
  } = useUserAnalyticsEngagementSummary({ filters: appliedFilters });
  const { mutateAsync: exportUserAnalyticsRows, isPending: isExporting } = useExportUserAnalyticsRows();
  const {
    data: filterOptionsRaw = {},
    isError: isFilterOptionsError,
    isFetching: isFilterOptionsFetching,
    refetch: refetchFilterOptions,
  } = useUserAnalyticsFilterOptions({ filters: appliedFilters });

  const filterOptions =
    /** @type {{siteIds?: string[], serviceAreas?: string[], serviceActions?: string[], outcomes?: string[], cancellationReasons?: string[]}} */ (
      filterOptionsRaw && typeof filterOptionsRaw === 'object' ? filterOptionsRaw : {}
    );

  const rows = isRowsError ? [] : normalizeAnalyticsRows(analyticsRows);
  const isError = isRowsError || isCountError || isFilterOptionsError || isEngagementSummaryError;
  const isRefreshing = isRowsFetching || isEngagementSummaryFetching || isCountFetching || isFilterOptionsFetching;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showingStart = !totalCount ? 0 : currentPage * PAGE_SIZE + 1;
  const showingEnd = Math.min(totalCount, currentPage * PAGE_SIZE + rows.length);
  const siteOptions = normalizeFilterOptions(filterOptions.siteIds, siteFilter);
  const serviceAreaOptions = normalizeFilterOptions(filterOptions.serviceAreas, serviceAreaFilter);
  const serviceActionOptions = normalizeFilterOptions(filterOptions.serviceActions, serviceActionFilter);
  const outcomeOptions = normalizeFilterOptions(filterOptions.outcomes, outcomeFilter);

  const reloadPage = () => {
    setStartDateFilter('');
    setEndDateFilter('');
    setAppliedStartDateFilter('');
    setAppliedEndDateFilter('');
    setDateInputResetToken((value) => value + 1);
    setAppliedFilters((currentFilters) => {
      const nextFilters = { ...currentFilters };
      delete nextFilters.fromLastEntryAt;
      delete nextFilters.toLastEntryAt;
      return nextFilters;
    });

    refetchRows();
    refetchEngagementSummary();
    refetchCount();
    refetchFilterOptions();
  };

  const buildFiltersFromInputs = () => {
    const normalizeText = (value) => {
      const normalizedValue = typeof value === 'string' ? value.trim() : '';
      return normalizedValue || undefined;
    };

    const normalizedStartDateFilter = normalizeDateInput(startDateFilter);
    const normalizedEndDateFilter = normalizeDateInput(endDateFilter);

    const fromLastEntryAt = normalizedStartDateFilter ? `${normalizedStartDateFilter}T00:00:00.000Z` : undefined;
    const toLastEntryAt = normalizedEndDateFilter ? `${normalizedEndDateFilter}T23:59:59.999Z` : undefined;

    return {
      empNumber: normalizeText(employeeFilter),
      siteId: normalizeText(siteFilter),
      serviceArea: normalizeText(serviceAreaFilter),
      serviceAction: normalizeText(serviceActionFilter),
      outcome: normalizeText(outcomeFilter),
      fromLastEntryAt,
      toLastEntryAt,
    };
  };

  const applyFilters = () => {
    const normalizedStartDateFilter = normalizeDateInput(startDateFilter);
    const normalizedEndDateFilter = normalizeDateInput(endDateFilter);
    const hasInvalidDateInput =
      (startDateFilter && !normalizedStartDateFilter) || (endDateFilter && !normalizedEndDateFilter);
    const hasInvalidDateRange =
      normalizedStartDateFilter &&
      normalizedEndDateFilter &&
      new Date(normalizedStartDateFilter).getTime() > new Date(normalizedEndDateFilter).getTime();

    if (hasInvalidDateInput || hasInvalidDateRange) return;

    setCurrentPage(0);
    setAppliedFilters(buildFiltersFromInputs());
    setAppliedStartDateFilter(normalizedStartDateFilter || '');
    setAppliedEndDateFilter(normalizedEndDateFilter || '');
  };

  const resetFilters = () => {
    setEmployeeFilter('');
    setSiteFilter('');
    setServiceAreaFilter('');
    setServiceActionFilter('');
    setOutcomeFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setAppliedStartDateFilter('');
    setAppliedEndDateFilter('');
    setDateInputResetToken((value) => value + 1);
    setCurrentPage(0);
    setAppliedFilters({});
  };

  const exportFilteredRows = async () => {
    try {
      const { filename, csv } = await exportUserAnalyticsRows(appliedFilters);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      downloadBlobAsFile(blob, filename);
    } catch {
      // Export failures surface via isExporting returning to false; the user can retry.
    }
  };

  const closeQaModal = () => {
    setSelectedQaRow(null);
  };

  const analyticsSummary = useMemo(() => {
    const summary =
      /** @type {{ sessionsStarted?: number, reachedOutcome?: number, submittedCount?: number, cancelledCount?: number, submittedWithQaCount?: number, highQualitySubmittedCount?: number, qaAssessedCount?: number, qaSpentTimeCount?: number, dailyTrend?: Array<{ day: string, sessionsCount: number, highQualityCount: number }> }} */ (
        engagementSummary && typeof engagementSummary === 'object' ? engagementSummary : {}
      );
    const sessionsStarted = summary.sessionsStarted ?? 0;
    const reachedOutcome = summary.reachedOutcome ?? 0;
    const submittedCount = summary.submittedCount ?? 0;
    const cancelledCount = summary.cancelledCount ?? 0;
    const submittedWithQaCount = summary.submittedWithQaCount ?? 0;
    const highQualitySubmittedCount = summary.highQualitySubmittedCount ?? 0;
    const qaAssessedCount = summary.qaAssessedCount ?? 0;
    const qaSpentTimeCount = summary.qaSpentTimeCount ?? 0;
    const dailyTrend = Array.isArray(summary.dailyTrend) ? summary.dailyTrend : [];

    const spentTimeRate = qaAssessedCount ? Math.round((qaSpentTimeCount / qaAssessedCount) * 100) : null;

    const qualityTrendData = dailyTrend.slice(-12).map((entry) => {
      const dayStart = new Date(`${entry.day}T00:00:00`);

      return {
        dayStart,
        sessions: entry.sessionsCount,
        highQuality: entry.highQualityCount,
        rate: entry.sessionsCount > 0 ? Math.round((entry.highQualityCount / entry.sessionsCount) * 100) : 0,
        label: formatShortDate(dayStart),
      };
    });

    return {
      submittedCount,
      cancelledCount,
      spentTimeRate,
      funnelSteps: [
        { label: 'Sessions Started', count: sessionsStarted },
        { label: 'Reached Outcome', count: reachedOutcome },
        { label: 'Submitted', count: submittedCount },
        { label: 'Submitted + Q&A', count: submittedWithQaCount },
        { label: 'High-Quality (Spent Time)', count: highQualitySubmittedCount },
      ],
      qualityTrendData,
    };
  }, [engagementSummary]);

  const usageTrend = useMemo(() => {
    const summary =
      /** @type {{ dailyTrend?: Array<{ day: string, sessionsCount: number, highQualityCount: number }> }} */ (
        engagementSummary && typeof engagementSummary === 'object' ? engagementSummary : {}
      );
    const dayUsageCounts = {};
    const dailyTrend = Array.isArray(summary.dailyTrend) ? summary.dailyTrend : [];
    for (const entry of dailyTrend) {
      const dayStart = new Date(`${entry.day}T00:00:00`);
      if (Number.isNaN(dayStart.getTime())) continue;

      dayUsageCounts[dayStart.toISOString()] = entry.sessionsCount;
    }

    const normalizedStartDateFilter = normalizeDateInput(appliedStartDateFilter);
    const normalizedEndDateFilter = normalizeDateInput(appliedEndDateFilter);
    const startBoundaryDate = normalizedStartDateFilter ? getDayStart(`${normalizedStartDateFilter}T00:00:00`) : null;
    const endBoundaryDate = normalizedEndDateFilter ? getDayStart(`${normalizedEndDateFilter}T00:00:00`) : null;

    const usageDatesFromRows = Object.keys(dayUsageCounts)
      .map((dayKey) => getDayStart(dayKey))
      .filter((value) => value !== null)
      .toSorted((a, b) => a.getTime() - b.getTime());

    const inferredStartDate = startBoundaryDate || usageDatesFromRows[0] || null;
    const inferredEndDate = endBoundaryDate || usageDatesFromRows.at(-1) || null;

    const rangeStart =
      inferredStartDate && inferredEndDate && inferredStartDate > inferredEndDate ? inferredEndDate : inferredStartDate;
    const rangeEnd =
      inferredStartDate && inferredEndDate && inferredStartDate > inferredEndDate ? inferredStartDate : inferredEndDate;

    const maxUsageWindowDays = 366;
    let usageDays = rangeStart && rangeEnd ? getDaysBetween(rangeStart, rangeEnd) : [];
    if (rangeEnd && usageDays.length > maxUsageWindowDays) {
      const chartWindowStart = new Date(rangeEnd.getTime() - (maxUsageWindowDays - 1) * 24 * 60 * 60 * 1000);
      usageDays = getDaysBetween(chartWindowStart, rangeEnd);
    }

    const usageTrendData = usageDays
      .map((dayStart) => {
        const dayKey = dayStart.toISOString();
        return {
          dayStart,
          usage: dayUsageCounts[dayKey] || 0,
          label: formatShortDate(dayStart),
        };
      })
      .slice(-maxUsageWindowDays);

    const lineChartInnerWidth = USAGE_LINE_CHART_WIDTH - USAGE_LINE_CHART_PADDING.left - USAGE_LINE_CHART_PADDING.right;
    const lineChartInnerHeight =
      USAGE_LINE_CHART_HEIGHT - USAGE_LINE_CHART_PADDING.top - USAGE_LINE_CHART_PADDING.bottom;
    const maxUsage = Math.max(1, ...usageTrendData.map((entry) => entry.usage));
    const usagePoints = usageTrendData.map((entry, index) => {
      const x =
        usageTrendData.length <= 1
          ? USAGE_LINE_CHART_PADDING.left + lineChartInnerWidth / 2
          : USAGE_LINE_CHART_PADDING.left + (index / (usageTrendData.length - 1)) * lineChartInnerWidth;
      const y = USAGE_LINE_CHART_PADDING.top + lineChartInnerHeight - (entry.usage / maxUsage) * lineChartInnerHeight;

      return { ...entry, x, y };
    });

    return {
      usageTrendData,
      usagePoints,
      usageLinePath: buildUsageLinePath(usagePoints),
      lineChartInnerWidth,
      lineChartInnerHeight,
      maxUsage,
    };
  }, [engagementSummary, appliedStartDateFilter, appliedEndDateFilter]);

  const hoveredUsageMessage = hoveredUsagePoint
    ? `${hoveredUsagePoint.label}: ${hoveredUsagePoint.usage}`
    : 'Hover a point to view count.';
  let modalOutcomeLabel = 'N/A';
  if (selectedQaRow?.outcome === 'submitted') {
    modalOutcomeLabel = 'Submitted';
  } else if (selectedQaRow?.outcome === 'cancelled') {
    modalOutcomeLabel = 'Cancelled';
  }

  return (
    <Container>
      <div className={styles.headerRow}>
        <h2>User Analytics</h2>
        <div className={styles.filterRow}>
          <label className={styles.dateFilterLabel}>
            Employee #
            <input
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className={styles.filterTextInput}
              placeholder='e.g. 012345'
            />
          </label>
          <div className={styles.dateFilterLabel}>
            <span>SiteID</span>
            <FilterChecklistDropdown
              label='SiteID'
              allLabel='All Sites'
              options={siteOptions}
              selectedValue={siteFilter}
              onChange={setSiteFilter}
            />
          </div>
          <div className={styles.dateFilterLabel}>
            <span>Service Area</span>
            <FilterChecklistDropdown
              label='Service Area'
              allLabel='All Areas'
              options={serviceAreaOptions}
              selectedValue={serviceAreaFilter}
              onChange={setServiceAreaFilter}
            />
          </div>
          <div className={styles.dateFilterLabel}>
            <span>Service Action</span>
            <FilterChecklistDropdown
              label='Service Action'
              allLabel='All Actions'
              options={serviceActionOptions}
              selectedValue={serviceActionFilter}
              onChange={setServiceActionFilter}
            />
          </div>
          <div className={styles.dateFilterLabel}>
            <span>Outcome</span>
            <FilterChecklistDropdown
              label='Outcome'
              allLabel='All Outcomes'
              options={outcomeOptions}
              selectedValue={outcomeFilter}
              onChange={setOutcomeFilter}
            />
          </div>
          <div className={styles.dateFilterGroup}>
            <label className={styles.dateFilterLabel}>
              Start
              <input
                type='date'
                key={`start-date-${dateInputResetToken}`}
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                className={styles.dateFilterInput}
              />
            </label>
            <label className={styles.dateFilterLabel}>
              End
              <input
                type='date'
                key={`end-date-${dateInputResetToken}`}
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                className={styles.dateFilterInput}
              />
            </label>
          </div>
          <Button size='sm' color='primary' onClick={applyFilters}>
            Apply Filters
          </Button>
          <Button size='sm' color='secondary' onClick={resetFilters}>
            Reset
          </Button>
          <Button size='sm' color='secondary' onClick={reloadPage}>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            size='sm'
            color='secondary'
            onClick={exportFilteredRows}
            disabled={!totalCount || isRefreshing || isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {isError ? <p className={styles.filterInfo}>Unable to load analytics data. Try refreshing the page.</p> : null}

      {!totalCount && !rows.length ? (
        <p>No analytics rows have been captured yet.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <div className={styles.metricsGrid}>
            <article className={styles.metricCard}>
              <p className={styles.metricTitle}>Completion : Cancellation</p>
              <p className={styles.metricRatio}>
                <span className={styles.metricPositive}>{analyticsSummary.submittedCount}</span>
                <span> : </span>
                <span className={styles.metricNegative}>{analyticsSummary.cancelledCount}</span>
              </p>
              <p className={styles.metricHint}>Based on all filtered rows</p>
            </article>
            <article className={styles.metricCard}>
              <p className={styles.metricTitle}>Spent Time Rate</p>
              <p className={styles.metricRatio}>
                {analyticsSummary.spentTimeRate === null ? 'N/A' : `${analyticsSummary.spentTimeRate}%`}
              </p>
              <p className={styles.metricHint}>From all filtered rows with Q&amp;A engagement data</p>
            </article>
          </div>
          <section className={styles.usageSection}>
            <article className={styles.usageCard}>
              <h3>Quality Usage Funnel</h3>
              <div className={styles.funnelList}>
                {analyticsSummary.funnelSteps.map((step, index) => {
                  const previousStepCount =
                    index === 0 ? analyticsSummary.funnelSteps[0].count : analyticsSummary.funnelSteps[index - 1].count;
                  const conversionRate = previousStepCount > 0 ? Math.round((step.count / previousStepCount) * 100) : 0;

                  return (
                    <div className={styles.funnelItem} key={step.label}>
                      <div className={styles.funnelItemMeta}>
                        <span>{step.label}</span>
                        <span>
                          {step.count} {index === 0 ? '' : `(${conversionRate}%)`}
                        </span>
                      </div>
                      <div className={styles.funnelBarTrack}>
                        <div
                          className={styles.funnelBarFill}
                          style={{
                            width: `${
                              analyticsSummary.funnelSteps[0].count > 0
                                ? Math.round((step.count / analyticsSummary.funnelSteps[0].count) * 100)
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className={styles.usageCard}>
              <h3>High-Quality Usage Rate Trend</h3>
              {!analyticsSummary.qualityTrendData.length ? (
                <p className={styles.usageEmpty}>No trend data in selected filters.</p>
              ) : (
                <div className={styles.trendList}>
                  {analyticsSummary.qualityTrendData.map((item) => (
                    <div className={styles.trendItem} key={item.dayStart.toISOString()}>
                      <span className={styles.trendLabel}>{item.label}</span>
                      <div className={styles.trendBarTrack}>
                        <div className={styles.trendBarFill} style={{ width: `${item.rate}%` }} />
                      </div>
                      <span className={styles.trendValue}>{item.rate}%</span>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className={styles.usageCard}>
              <h3>Daily Usage Trend</h3>
              {!usageTrend.usageTrendData.length ? (
                <p className={styles.usageEmpty}>No usage data in selected date range.</p>
              ) : (
                <div className={styles.lineChartWrapper}>
                  <p className={styles.lineChartHoverValue}>Usage {hoveredUsageMessage}</p>
                  <svg
                    viewBox={`0 0 ${USAGE_LINE_CHART_WIDTH} ${USAGE_LINE_CHART_HEIGHT}`}
                    role='img'
                    aria-label='Daily usage trend'
                    className={styles.lineChartSvg}
                  >
                    <line
                      x1={USAGE_LINE_CHART_PADDING.left}
                      y1={USAGE_LINE_CHART_PADDING.top + usageTrend.lineChartInnerHeight}
                      x2={USAGE_LINE_CHART_PADDING.left + usageTrend.lineChartInnerWidth}
                      y2={USAGE_LINE_CHART_PADDING.top + usageTrend.lineChartInnerHeight}
                      className={styles.lineChartAxis}
                    />
                    <line
                      x1={USAGE_LINE_CHART_PADDING.left}
                      y1={USAGE_LINE_CHART_PADDING.top}
                      x2={USAGE_LINE_CHART_PADDING.left}
                      y2={USAGE_LINE_CHART_PADDING.top + usageTrend.lineChartInnerHeight}
                      className={styles.lineChartAxis}
                    />

                    {[
                      ...new Set(
                        [0, Math.ceil(usageTrend.maxUsage / 2), usageTrend.maxUsage].map((tickValue) => tickValue),
                      ),
                    ].map((tickValue) => {
                      const y =
                        USAGE_LINE_CHART_PADDING.top +
                        usageTrend.lineChartInnerHeight -
                        (tickValue / usageTrend.maxUsage) * usageTrend.lineChartInnerHeight;

                      return (
                        <g key={`tick-${tickValue}`}>
                          <line
                            x1={USAGE_LINE_CHART_PADDING.left}
                            y1={y}
                            x2={USAGE_LINE_CHART_PADDING.left + usageTrend.lineChartInnerWidth}
                            y2={y}
                            className={styles.lineChartGridline}
                          />
                          <text x={USAGE_LINE_CHART_PADDING.left - 8} y={y + 4} className={styles.lineChartTickLabel}>
                            {tickValue}
                          </text>
                        </g>
                      );
                    })}

                    <path d={usageTrend.usageLinePath} className={styles.lineChartPath} />
                    {usageTrend.usagePoints.map((point, index) => {
                      const shouldLabel =
                        usageTrend.usagePoints.length <= 4 ||
                        index === 0 ||
                        index === usageTrend.usagePoints.length - 1 ||
                        index % 4 === 0;

                      return (
                        <g key={point.dayStart.toISOString()}>
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r='4.5'
                            aria-label={`Usage point ${point.label}: ${point.usage}`}
                            className={`${styles.lineChartPoint} ${
                              hoveredUsagePoint?.dayKey === point.dayStart.toISOString()
                                ? styles.lineChartPointActive
                                : ''
                            }`}
                            tabIndex='0'
                            onMouseEnter={() =>
                              setHoveredUsagePoint({
                                dayKey: point.dayStart.toISOString(),
                                label: point.label,
                                usage: point.usage,
                              })
                            }
                            onMouseLeave={() => setHoveredUsagePoint(null)}
                            onFocus={() =>
                              setHoveredUsagePoint({
                                dayKey: point.dayStart.toISOString(),
                                label: point.label,
                                usage: point.usage,
                              })
                            }
                            onBlur={() => setHoveredUsagePoint(null)}
                          >
                            <title>
                              {point.label}: {point.usage}
                            </title>
                          </circle>
                          {shouldLabel ? (
                            <text
                              x={point.x}
                              y={USAGE_LINE_CHART_PADDING.top + usageTrend.lineChartInnerHeight + 16}
                              className={styles.lineChartDateLabel}
                            >
                              {point.label}
                            </text>
                          ) : null}
                        </g>
                      );
                    })}
                  </svg>
                  <p className={styles.lineChartFootnote}>Counts per day for rows in the selected date range.</p>
                </div>
              )}
            </article>
          </section>
          <p className={styles.heuristicNote}>
            * Q&amp;A engagement is marked as Spent time when additional text is typed, when total page time reaches 75
            seconds, or when answered questions span at least 20 seconds or total at least 35 seconds. It is marked as
            Partial when there is at least one quick response and one long pause between answers. Otherwise it is marked
            as Quick pass.
          </p>
          <div className={styles.paginationSummary}>
            <p className={styles.filterInfo}>
              Showing {showingStart}-{showingEnd} of {totalCount} filtered entries
            </p>
            <div className={styles.paginationControls}>
              <Button
                size='sm'
                color='secondary'
                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                disabled={currentPage === 0 || isRefreshing}
              >
                Previous
              </Button>
              <span className={styles.paginationLabel}>
                Page {Math.min(currentPage + 1, totalPages)} of {totalPages}
              </span>
              <Button
                size='sm'
                color='secondary'
                onClick={() => setCurrentPage((prev) => (prev + 1 < totalPages ? prev + 1 : prev))}
                disabled={currentPage + 1 >= totalPages || isRefreshing}
              >
                Next
              </Button>
            </div>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Employee #</th>
                <th>Equipment #</th>
                <th>Site</th>
                <th>Service Area</th>
                <th>Service Action</th>
                <th>Comment</th>
                <th>Outcome</th>
                <th>Questions &amp; Answers</th>
                <th>Cancellation Reason</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length ? (
                <tr>
                  <td colSpan='10' className={styles.emptyMessage}>
                    No results match the selected filter.
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const isOutcomeRow = Boolean(row.outcome);
                  const qaAssessment = getQaEngagementAssessment(row);
                  let outcomeLabel = '—';
                  if (isOutcomeRow) {
                    outcomeLabel = row.outcome === 'submitted' ? 'Submitted' : 'Cancelled';
                  }
                  return (
                    <tr
                      key={row.id || `analytics-row-${row.lastEntryAt || row.empNumber || index}`}
                      className={isOutcomeRow ? styles.outcomeRow : styles.continueRow}
                    >
                      <td>{formatDateTime(row.lastEntryAt)}</td>
                      <td>{row.empNumber || 'N/A'}</td>
                      <td>{row.equipmentNumber || 'N/A'}</td>
                      <td>{row.siteId || 'N/A'}</td>
                      <td>{row.serviceArea || 'N/A'}</td>
                      <td>{row.serviceAction || 'N/A'}</td>
                      <td>{row.comment || 'N/A'}</td>
                      <td>{outcomeLabel}</td>
                      <td className={styles.qaCell}>
                        {isOutcomeRow && Array.isArray(row.questionAnswers) && row.questionAnswers.length > 0 ? (
                          <div className={styles.qaActionRow}>
                            <Button size='sm' color='secondary' onClick={() => setSelectedQaRow(row)}>
                              View
                            </Button>
                            {qaAssessment ? (
                              <span className={qaAssessment.toneClassName}>{qaAssessment.label}</span>
                            ) : null}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{isOutcomeRow ? row.cancellationReason || '—' : '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <ModalRoot open={Boolean(selectedQaRow)} onOpenChange={(isOpen) => !isOpen && closeQaModal()}>
        <Modal>
          <ModalHeader>Questions &amp; Answers</ModalHeader>
          <ModalContent>
            {selectedQaRow ? (
              <div className={styles.qaModalContent}>
                <div className={styles.qaModalMeta}>
                  <span>
                    <strong>Employee #:</strong> {selectedQaRow.empNumber || 'N/A'}
                  </span>
                  <span>
                    <strong>Equipment #:</strong> {selectedQaRow.equipmentNumber || 'N/A'}
                  </span>
                  <span>
                    <strong>Outcome:</strong> {modalOutcomeLabel}
                  </span>
                  <span>
                    <strong>Arrived at page:</strong> {formatTime(selectedQaRow.arrivedAt) || 'N/A'}
                  </span>
                </div>

                <div className={styles.qaList}>
                  {selectedQaRow.questionAnswers.map((qa, index) => (
                    <section
                      key={`${selectedQaRow.id}-${qa.question || 'question'}-${qa.answeredAt || index}`}
                      className={styles.qaCard}
                    >
                      <div className={styles.qaCardHeader}>
                        <h3>Question {index + 1}</h3>
                        <span>{qa.answeredAt ? formatTime(qa.answeredAt) : 'Time not captured'}</span>
                      </div>
                      <p className={styles.qaQuestion}>{qa.question || 'Question unavailable'}</p>
                      <p>
                        <strong>Answer:</strong> {qa.answer || 'N/A'}
                      </p>
                      {qa.additionalDetails ? (
                        <p>
                          <strong>Additional details:</strong> {qa.additionalDetails}
                        </p>
                      ) : null}
                    </section>
                  ))}
                </div>
              </div>
            ) : null}
          </ModalContent>
          <ModalFooter>
            <Button size='sm' color='secondary' onClick={closeQaModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </ModalRoot>
    </Container>
  );
};

export default Analytics;
